import { useState, useEffect, useCallback } from 'react'
import {
  Search,
  Loader2,
  X,
  ChevronLeft,
  ChevronRight,
  Network,
} from 'lucide-react'
import { usePlanWizardStore } from '@/stores/usePlanWizardStore'
import { usePlanogramSearch } from '@/hooks/usePlanogramSearch'
import { usePlanogramSkus } from '@/hooks/usePlanogramSkus'
import { cn } from '@/utils/cn'
import { StepHeader } from './StepHeader'
import { ProcessingOverlay } from '@/components/common/ProcessingOverlay'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { Planogram } from '@/api/planograms'
import type { PlanogramProductListStatus } from '@/hooks/usePlanogramProductList'

const PAGE_SIZE = 5

interface PlanogramSourceStepProps {
  processingStatus: PlanogramProductListStatus
  processingProgress: number
  processingError: string | null
}

export function PlanogramSourceStep({
  processingStatus,
  processingProgress,
  processingError,
}: PlanogramSourceStepProps) {
  const {
    selectedPlanograms,
    planogramSkus,
    planogramSkusTotalCount,
    planogramSubStep,
    addPlanogram,
    removePlanogram,
    setPlanogramSkus,
    removePlanogramSku,
  } = usePlanWizardStore()

  const [query, setQuery] = useState('')
  const [skuPage, setSkuPage] = useState(1)

  const { data: planogramData, isLoading: isSearching } =
    usePlanogramSearch(query)
  const planograms = planogramData?.data ?? []

  const pogIds = selectedPlanograms.map(p => p.pog_id)
  const pogIdsKey = pogIds.slice().sort().join(',')
  const { data: skusData, isLoading: isLoadingSkus } = usePlanogramSkus(pogIds)

  // Clear SKUs immediately when the planogram selection changes so stale rows
  // don't linger while the new query is in-flight
  useEffect(() => {
    setPlanogramSkus([], 0)
    setSkuPage(1)
  }, [pogIdsKey, setPlanogramSkus])

  // Populate SKUs once the query returns data
  useEffect(() => {
    if (skusData) {
      setPlanogramSkus(skusData.data, skusData.data.length)
      setSkuPage(1)
    }
  }, [skusData, setPlanogramSkus])

  const handleToggle = useCallback(
    (planogram: Planogram) => {
      const isSelected = selectedPlanograms.some(
        p => p.pog_id === planogram.pog_id
      )
      if (isSelected) {
        removePlanogram(String(planogram.pog_id))
      } else {
        addPlanogram({ ...planogram, id: String(planogram.pog_id) })
      }
    },
    [selectedPlanograms, addPlanogram, removePlanogram]
  )

  const totalSkuPages = Math.ceil(planogramSkusTotalCount / PAGE_SIZE)
  const pagedSkus = planogramSkus.slice(
    (skuPage - 1) * PAGE_SIZE,
    skuPage * PAGE_SIZE
  )
  const rangeStart =
    planogramSkus.length === 0 ? 0 : (skuPage - 1) * PAGE_SIZE + 1
  const rangeEnd = Math.min(skuPage * PAGE_SIZE, planogramSkus.length)

  // Processing overlay
  if (processingStatus === 'creating' || processingStatus === 'processing') {
    return (
      <div className="flex flex-col gap-6 w-full">
        <StepHeader
          title="Select Planograms"
          description="Creating product list from selected planograms…"
        />
        <ProcessingOverlay
          status={processingStatus === 'creating' ? 'creating' : 'processing'}
          progress={processingProgress}
          fileName={`${selectedPlanograms.length} planogram(s)`}
          error={null}
        />
      </div>
    )
  }

  if (processingStatus === 'error') {
    return (
      <div className="flex flex-col gap-6 w-full">
        <StepHeader
          title="Select Planograms"
          description="There was an error processing the planograms"
        />
        <ProcessingOverlay
          status="error"
          progress={0}
          fileName={null}
          error={processingError}
        />
      </div>
    )
  }

  // ── Sub-step: Select ──────────────────────────────────────────────────────
  if (planogramSubStep === 'select') {
    return (
      <div className="flex flex-col gap-6 w-full">
        <StepHeader
          title="Select Planograms"
          description="Choose a planogram to start"
        />

        <div className="bg-white rounded-[12px] border border-[#D6DAE1] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.09)] overflow-hidden">
          <div className="p-4 pb-0">
            <h3 className="text-[16px] font-semibold text-[#1C1C1C] mb-3">
              Select Planograms
            </h3>

            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#556374]" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Type a command or search..."
                className={cn(
                  'w-full h-10 pl-9 pr-4 text-[14px] bg-[#F8F8F8] border border-[#D6DAE1] rounded-[8px]',
                  'placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#E53935]/50'
                )}
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#556374] animate-spin" />
              )}
            </div>
          </div>

          {/* Suggestions — always visible */}
          <div className="border-t border-[#D6DAE1] mt-4 max-h-[320px] overflow-y-auto">
            <div className="px-4 py-2">
              <span className="text-[13px] font-semibold text-[#556374]">
                Suggestions
              </span>
            </div>
            {isSearching ? (
              <div className="px-4 py-6 text-center text-[#556374]">
                <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                <span className="text-[14px]">Loading...</span>
              </div>
            ) : planograms.length === 0 ? (
              <div className="px-4 py-6 text-center text-[14px] text-[#556374]">
                No planograms found
              </div>
            ) : (
              <ul className="px-2 pb-2">
                {planograms.map(planogram => {
                  const isSelected = selectedPlanograms.some(
                    p => p.pog_id === planogram.pog_id
                  )
                  return (
                    <li key={planogram.pog_id}>
                      <button
                        type="button"
                        onClick={() => handleToggle(planogram)}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2 rounded-[6px] cursor-pointer',
                          'text-left transition-colors duration-150',
                          isSelected ? 'bg-[#FEF2F2]' : 'hover:bg-[#F8F8F8]'
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          className="h-4 w-4 rounded border-[#D6DAE1] accent-[#E53935] cursor-pointer flex-shrink-0"
                        />
                        <Network className="h-4 w-4 text-[#556374] flex-shrink-0" />
                        <span
                          className={cn(
                            'flex-1 text-[14px] truncate',
                            isSelected
                              ? 'font-medium text-[#E53935]'
                              : 'text-[#1C1C1C]'
                          )}
                        >
                          {planogram.name}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    )
  }

  // ── Sub-step: Review ─────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-6 w-full">
      <StepHeader
        title="Select Planograms"
        description="Choose a planogram to start"
      />

      {/* Selected planograms card */}
      <div className="bg-white rounded-[12px] border border-[#D6DAE1] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.09)] overflow-hidden">
        <div className="p-4">
          <h3 className="text-[16px] font-semibold text-[#1C1C1C] mb-3">
            Select Planograms
          </h3>

          <div className="flex flex-col gap-1">
            {selectedPlanograms.map(p => (
              <div
                key={p.pog_id}
                className="flex items-center justify-between px-3 py-2 bg-[#FEF2F2] border border-[#E53935] rounded-[6px]"
              >
                <span className="text-[14px] font-medium text-[#E53935] truncate">
                  {p.name}
                </span>
                <button
                  type="button"
                  onClick={() => removePlanogram(String(p.pog_id))}
                  className="text-[#E53935] hover:text-[#b71c1c] cursor-pointer flex-shrink-0 ml-2"
                  aria-label={`Remove ${p.name}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Imported SKUs section */}
      <div className="bg-white rounded-[12px] border border-[#D6DAE1] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.09)] overflow-hidden">
        <div className="px-6 pt-5 pb-3">
          <h3 className="text-[16px] font-semibold text-[#1C1C1C]">
            Imported SKUs
          </h3>
          {isLoadingSkus ? (
            <div className="flex items-center gap-2 mt-1">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-[#556374]" />
              <span className="text-[13px] text-[#556374]">Fetching SKUs…</span>
            </div>
          ) : (
            <p className="text-[13px] text-[#556374] mt-0.5">
              {planogramSkusTotalCount} Items Imported
            </p>
          )}
        </div>

        {!isLoadingSkus && planogramSkus.length > 0 && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6 text-[13px] text-[#556374]">
                    SKU #
                  </TableHead>
                  <TableHead className="text-[13px] text-[#556374]">
                    Description
                  </TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedSkus.map(sku => (
                  <TableRow key={sku.sku_number}>
                    <TableCell className="pl-6 text-[14px] font-medium text-[#1C1C1C]">
                      {sku.sku_number}
                    </TableCell>
                    <TableCell className="text-[14px] text-[#1C1C1C]">
                      {sku.description ?? '—'}
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => {
                          removePlanogramSku(sku.sku_number)
                          if (skuPage > 1 && pagedSkus.length === 1) {
                            setSkuPage(p => p - 1)
                          }
                        }}
                        className="text-[#E53935] hover:text-[#b71c1c] cursor-pointer"
                        aria-label={`Remove SKU ${sku.sku_number}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-end gap-3 px-6 py-3 border-t border-[#D6DAE1]">
              <span className="text-[13px] text-[#556374]">Rows per page</span>
              <span className="text-[13px] font-medium text-[#1C1C1C]">
                {PAGE_SIZE}
              </span>
              <span className="text-[13px] text-[#556374]">
                {rangeStart}–{rangeEnd}
              </span>
              <button
                type="button"
                disabled={skuPage <= 1}
                onClick={() => setSkuPage(p => Math.max(p - 1, 1))}
                className={cn(
                  'flex items-center justify-center h-6 w-6 rounded',
                  skuPage <= 1
                    ? 'text-[#D6DAE1] cursor-not-allowed'
                    : 'text-[#556374] hover:bg-[#F8F8F8] cursor-pointer'
                )}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={skuPage >= totalSkuPages}
                onClick={() => setSkuPage(p => Math.min(p + 1, totalSkuPages))}
                className={cn(
                  'flex items-center justify-center h-6 w-6 rounded',
                  skuPage >= totalSkuPages
                    ? 'text-[#D6DAE1] cursor-not-allowed'
                    : 'text-[#556374] hover:bg-[#F8F8F8] cursor-pointer'
                )}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
